<template>
  <div class="gps-links">
    <p class="gl-intro">
      GPS athletes that aren't yet matched to a player. Until an athlete is
      linked, their data can't appear on a profile or in benchmarks.
      <strong>Only link where you're certain</strong> — a wrong link puts one
      player's physical data on another player's profile.
    </p>

    <div class="gl-bar">
      <label class="gl-toggle">
        <input type="checkbox" v-model="includeIgnored" @change="load" />
        Show dismissed
      </label>
      <span class="gl-count" v-if="!loading">
        {{ athletes.length }} to review<template v-if="provider"> · {{ provider }}</template>
      </span>
    </div>

    <div v-if="loading" class="gl-msg">Loading…</div>
    <div v-else-if="error" class="gl-msg gl-error">{{ error }}</div>
    <div v-else-if="!athletes.length" class="gl-msg">
      Nothing to review — every GPS athlete is either linked or dismissed.
    </div>

    <table v-else class="gl-table">
      <thead>
        <tr><th>GPS name</th><th>Suggestions</th><th>Link to player</th><th></th></tr>
      </thead>
      <tbody>
        <tr v-for="a in athletes" :key="a.key" :class="{ 'gl-ignored': a.ignored == 1 }">
          <td>
            {{ a.name }}
            <span v-if="a.ambiguous" class="gl-flag">more than one possible match</span>
          </td>
          <td>
            <button
              v-for="s in a.suggestions"
              :key="s.id"
              class="gl-suggest"
              @click="link(a, s.id)"
            >{{ s.name }}</button>
            <span v-if="!a.suggestions.length" class="gl-none">—</span>
          </td>
          <td>
            <select :value="''" @change="link(a, $event.target.value)">
              <option value="">Choose a player…</option>
              <option v-for="p in players" :key="p.id" :value="p.id">{{ p.name }}</option>
            </select>
          </td>
          <td>
            <button v-if="a.ignored == 1" class="gl-btn" @click="restore(a)">Restore</button>
            <button v-else class="gl-btn gl-dismiss" @click="ignore(a)">Not an app player</button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { GpsLinks } from "@/services/gps-links-service.js";
import { getClubId } from "@/config/tenant";

export default {
  name: "GpsPlayerLinks",
  data() {
    return {
      loading: false,
      error: null,
      provider: null,
      athletes: [],
      players: [],
      includeIgnored: false,
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await GpsLinks.listUnlinked(getClubId(), this.includeIgnored);
        this.provider = data.provider;
        this.athletes = data.athletes || [];
        this.players = data.players || [];
      } catch (e) {
        this.error = "Could not load the athlete list.";
      } finally {
        this.loading = false;
      }
    },
    async link(a, playerId) {
      if (!playerId) return;
      const player = this.players.find((p) => String(p.id) === String(playerId));
      if (!player) return;
      // Confirmed because it is not obviously reversible from here, and the
      // cost of getting it wrong is a child's data on the wrong profile.
      if (!window.confirm(`Link GPS athlete "${a.name}" to ${player.name}?`)) return;
      await GpsLinks.link(getClubId(), a.key, playerId);
      this.athletes = this.athletes.filter((x) => x.key !== a.key);
    },
    async ignore(a) {
      await GpsLinks.ignore(getClubId(), a.key);
      if (this.includeIgnored) a.ignored = 1;
      else this.athletes = this.athletes.filter((x) => x.key !== a.key);
    },
    async restore(a) {
      await GpsLinks.restore(getClubId(), a.key);
      a.ignored = 0;
    },
  },
};
</script>

<style scoped>
.gps-links { padding: 0.5rem 0 2rem; }
.gl-intro { font-size: 0.85rem; color: #495057; max-width: 62ch; line-height: 1.5; }
.gl-bar { display: flex; align-items: center; gap: 1.25rem; padding: 0.75rem 0; }
.gl-toggle { font-size: 0.8rem; color: #6c757d; display: flex; align-items: center; gap: 0.35rem; }
.gl-count { font-size: 0.8rem; color: #6c757d; }
.gl-msg { padding: 1.25rem 0; color: #6c757d; font-size: 0.9rem; }
.gl-error { color: #dc3545; }

.gl-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
.gl-table th { text-align: left; font-size: 0.72rem; text-transform: uppercase; color: #868e96; padding: 0.4rem 0.5rem; border-bottom: 1px solid #dee2e6; }
.gl-table td { padding: 0.5rem; border-bottom: 1px solid #f1f3f5; vertical-align: middle; }
.gl-ignored { opacity: 0.5; }
.gl-flag { display: block; font-size: 0.7rem; color: #f6aa1e; }
.gl-none { color: #adb5bd; }

.gl-suggest { border: 1px solid #198754; background: #fff; color: #198754; border-radius: 5px; padding: 0.2rem 0.5rem; font-size: 0.78rem; cursor: pointer; margin-right: 0.3rem; }
.gl-suggest:hover { background: #198754; color: #fff; }
.gl-btn { border: 1px solid #ced4da; background: #fff; border-radius: 5px; padding: 0.25rem 0.6rem; font-size: 0.78rem; cursor: pointer; white-space: nowrap; }
.gl-dismiss:hover { border-color: #dc3545; color: #dc3545; }
select { padding: 0.3rem 0.5rem; border: 1px solid #ced4da; border-radius: 5px; font-size: 0.8rem; max-width: 15rem; }
</style>
